/**
 * Pixel-mask rigid bodies — issue #42, Phase 6.
 *
 * ==== WHAT IS NOT HERE, AND WHY THAT IS THE FEATURE =====================
 *
 * There is no marching squares, no Douglas-Peucker, no triangulation and no
 * polygon solver. `rigidShapes.ts` explains the argument at length; the short
 * form is that those three steps exist only to translate pixels into polygons
 * for a solver that cannot read pixels, and recompute-on-damage is the tax for
 * that translation being lossy. This solver reads pixels. A damaged body is a
 * body with fewer live pixels, and nothing has to be rebuilt.
 *
 * ==== FRAME ORDER, AND THE ONE PLACE WE IMPROVE ON THE TALK =============
 *
 *     rigid.lift() → cellSim.step() → rigid.step() → rigid.writeBack()
 *                  → particles.step() → reactions → thermal → charge
 *                  → pressure → light
 *
 * Only `cellSim` needs the body gone — it is the one pass that would see a
 * crate's wood as loose matter to be moved. Everything after write-back sees
 * the crate's **real WOOD cells sitting in the grid**, which is why a wooden
 * crate in a fire ignites, chars and structurally fails with *no rigid-body
 * fire code anywhere in this file*. `thermal` sets `Flag.BURNING` on the
 * crate's cells and burns down their `aux` fuel; `lift` reads all four cell
 * words back into the body on the next tick, so the burn survives the round
 * trip; when the wood finishes burning it becomes `ASH`, which is `LOOSE` and
 * therefore stops being part of the body (see `lift`); and when enough of the
 * body has stopped being part of it, `FAIL_FRACTION` breaks the rest apart.
 *
 * That the fuel counter (`aux`) is absorbed and restored is not a detail. The
 * first version wrote back through `CellWorld.set`, which re-seeds `aux` from
 * the material, and a burning crate therefore had its fuel gauge reset to full
 * every tick and burned forever without ever being consumed.
 *
 * ==== THE BINDING CONSTRAINT IS GPU UPLOAD, NOT CPU =====================
 *
 * A moving body dirties render tiles at its **old and its new** position. A
 * 13-cell crate straddles at most 2×2 of the 16×16 render tiles, so:
 *
 *     4 bodies × 4 tiles × 2 positions × 256 cells/tile = 8,192 cells/frame
 *
 * **#42 states this product as 16,384 and it is 8,192.** 16,384 is right about
 * the thing being compared to: it is one whole 128×128 chunk, which is what the
 * terrain renderer's dirty *rectangle* uploaded when fire scattered across a
 * chunk, and it is why `world.ts` grew a 16×16 dirty-**tile** mask in the first
 * place. Four awake bodies costs half that, and **eight** would cost all of it.
 *
 * The cap stays at four. It is an upload cap wearing a physics cap's clothes —
 * nothing about the CPU cost would object to forty, and the measured line is
 * 0.185 ms against a 3528-unit quota — and keeping a factor of two in hand
 * against the number that broke the budget once is the whole point of writing
 * the arithmetic down instead of inheriting it.
 *
 * Two things make the real figure much smaller than the bound. A body that is
 * **still but awake** is not lifted or written back at all (see `Body.resting`)
 * and dirties nothing; a body that is **asleep** is not in the active list, is
 * never lifted, is never written back, and its cells sit in the grid as
 * ordinary `Flag.STATIC` matter. Sleeping costs zero work units and zero
 * upload — `tests/rigid.test.ts` asserts the zero directly, because "cheap" is
 * not the claim, "free" is.
 *
 * **All three of those figures are now measured rather than argued.**
 * `tests/rigid.test.ts` counts set bits in `Chunk.renderTiles` — the same mask
 * `render/terrain.ts` uploads from — after one tick of the frame order:
 *
 *     four crates, all in flight     4,096 cells/tick peak   (bound 8,192)
 *     landed, still awake            0
 *     asleep                         0
 *
 * The flight figure is half the bound because a body moving one to four cells
 * overlaps most of its own previous tiles; the "two positions" term is a worst
 * case only a body teleporting a whole tile per tick would reach. So four awake
 * bodies cost a **quarter** of the 16,384 that blew the budget, and the factor
 * of two the cap was chosen to keep in hand is really a factor of four. That is
 * an argument for leaving the cap alone, not for raising it: the headroom is
 * what absorbs a shape larger than the crate.
 *
 * ==== TWO SNAGS, RECORDED RATHER THAN DISCOVERED ========================
 *
 * **1. There is no free flag bit** for "this cell belongs to a sleeping body".
 * `Flag` in `materials.ts` uses bits 0–12 and 13–15 are the variant field, so
 * all sixteen are spoken for. The ownership index is therefore a
 * `Map<packedCoord, bodyId>` (`sleepingCells` below), and it is read from
 * exactly one place: `CellWorld.onCellDamaged`, which fires inside `damageCell`.
 * Anything that tries to read it per tick will have reintroduced the per-tick
 * cost that sleeping exists to remove.
 *
 * **2. Grain used to crawl on a rotating body. Fixed by #121; the reasoning
 * that said it could not be is worth keeping, because it was wrong in an
 * instructive way.** `render/terrain.ts`'s `hash()` is keyed on *world*
 * coordinates, so a crate's wood grain was a property of where the crate was
 * rather than of the crate, and rotating one made the speckle swim. This note
 * used to say that fixing it "means the renderer knowing which cells belong to
 * which body, which is the ownership map above turned into a per-frame read —
 * the exact thing snag 1 says not to do."
 *
 * **The ownership map was never what the renderer needed.** It needed a
 * *coordinate space*, and `place()` below already establishes one every time
 * it runs: it walks the mask, so it can stamp each cell's grain into a small
 * world-aligned window (`BodyGrain`) for one store per pixel in a loop that
 * was already running. The byte it stamps is keyed on the pixel's **unrotated**
 * offset (`BakedShape.grain`), so it is the same byte at all 32 orientations
 * and there is no trigonometry in the renderer at all.
 *
 * So the ownership read is not per frame and not per cell — it is **per
 * placement**, which is when ownership actually changes. A body that is not
 * moving is not re-placed, so its window is written once and then holds still,
 * and the renderer skips its upload. Snag 1's cost was real; the conclusion
 * drawn from it was not, because it assumed the only shape the answer could
 * take was the one that was already there.
 *
 * `tests/rigidGrain.test.ts` measures the before and after on a tumbling crate.
 *
 * ==== DETERMINISM =======================================================
 *
 * docs/01 §8 and §9: no simulation system may schedule off a clock. This one
 * has a fixed work quota derived from the shipped shapes (see
 * `maxWorkPerTick`), takes no `Rng`, and calls no trigonometric function at
 * runtime — orientation is an integer index into masks baked from a table of
 * literals. `tests/rigid.test.ts` runs the same fixture twice and compares the
 * full body state word for word.
 *
 * ==== DOORS: THE DECISIONS, WRITTEN DOWN RATHER THAN LET FALL OUT =======
 *
 * The content order is crates → doors → minecarts. A door is the easy one on
 * purpose: **one axis of motion, so no rotation, so no re-baking and no grain
 * crawl** (snag 2 above never fires on a body that cannot turn). Mechanically
 * it is a body whose position is clamped to a segment, with a rest state at
 * each end. Four things about it were decided rather than discovered.
 *
 * **The door that ships is a hatch**, and that too is a decision rather than a
 * theme. Every sliding door needs a pocket to retract into, and the pocket has
 * to be perpendicular to the passage or the door sweeps through the thing it is
 * supposed to be blocking. For an upright gate in a corridor that pocket is a
 * shaft in the ceiling as tall as the doorway, which the generator does not
 * have and would have to be carved as a room; for a plate lying in a floor it
 * is a five-cell slot beside the hole, which `world/bodySites.ts` finds in a
 * real chunk. The hatch is also the geometry where suppressing gravity is
 * honest rather than arbitrary: the plate travels **perpendicular** to gravity
 * and the slot is what holds it up, so a door has no gravity term at all and
 * the closing speed is a property of the door instead of a property of how tall
 * the doorway happened to be.
 *
 * **1. A door is opened by contact. Not by damage, and not by a switch.**
 *
 * *Damage is not opening, it is destroying,* and this file already did it in
 * #42: any body below `FAIL_FRACTION` comes apart. A door that only opened by
 * being broken would be a wall with a hit-point bar — single use, and a tax on
 * every traversal. Breaking a door stays available and stays *destruction*.
 *
 * *A switch is two objects and a link between them.* The link is run state —
 * which switch opens which door — and it is the one kind of state
 * `world/bodySites.ts` cannot re-derive from the seed, because a thrown switch
 * has no cell footprint to read back. It also wants a pair of sites in
 * possibly different chunks, and the ledger keys on `(cx, cy, index)` with no
 * concept of a pair, and an "interact" verb the input layer does not have.
 * Three pieces of new machinery outside this solver, for a puzzle vocabulary
 * nothing has asked for.
 *
 * *Contact needs nothing new at all.* The entity layer is already reachable
 * through `CrushSink`, and **the door reuses that same query rather than
 * growing a sensor interface of its own** — which buys a real invariant:
 * nothing can hold a door open that the door cannot also see, because it is
 * one call. The query is asked about the **shut** pose, not the door's current
 * one: the thing being sensed is the doorway, and the doorway does not move
 * when the door does.
 *
 * **2. What happens when it is destroyed mid-swing.** Four claims, each of
 * which is a test in `tests/rigidDoor.test.ts`:
 *
 * - *The wreck lands where the door was, not where it started.* `breakApart`
 *   releases at `placedX/placedY`, which is the mid-swing pose.
 * - *The drive dies with the body.* The target is not held anywhere outside
 *   `Body.door`, and the failure check in `step` runs **before** anything is
 *   integrated, so a door that failed this tick is never driven again. A
 *   half-open door does not finish its swing as a corpse.
 * - *The wreck stops being a door.* Not "the doorway clears" — wood is not
 *   `LOOSE`, so the planks stand in the frame until something moves them,
 *   exactly as a broken crate's do. What changes is `Flag.STATIC`: the debris
 *   becomes diggable, burnable, displaceable matter, which is the whole of the
 *   difference between a door and a wall. And the ledger writes the site off as
 *   `gone`, so a door you burned through does not come back when its chunk is
 *   evicted and regenerates.
 * - *A door that fails while resting is still in the grid*, so `breakApart`'s
 *   in-grid branch is the one that runs and it does exactly one thing: stop
 *   being static. That is the common case for a door, because a door spends
 *   almost all of its life at a rest state.
 *
 * **3. A blocked door jams; it does not push.** `fitsDir` refuses the step and
 * the door simply does not move that tick, keeping its target and retrying.
 * This is the same sentence as "bodies do not push entities, they only crush
 * them", applied to matter: a hatch with sand in its recess will not open, and
 * that falls out of the collision probe with no jamming code. A jammed door
 * that is not moving is `resting` — one probe a tick and no upload — and will
 * sleep, because a door stuck shut is a floor. Digging out whatever jammed it
 * fires `damageCell`, which wakes it.
 *
 * **4. A door crushes on the same terms as a crate.** It drives at
 * `DOOR_SPEED`, which is deliberately above `CRUSH_SPEED`, so a closing door
 * is a hazard — and docs/01 §5.1's asymmetry applies unchanged, from the same
 * branch: it crushes an enemy and deals the player exactly zero.
 *
 * ==== MINECARTS ARE DECLINED, AND THIS IS THE ARITHMETIC ================
 *
 * They are last in the content order because they are the speculative one, and
 * the answer is no.
 *
 * A rail is a constraint this solver cannot express. A door's constraint is a
 * clamp on **one scalar along a world axis** — the whole of it is `at`,
 * `goal` and a `fitsDir` in one of four baked directions. A rail is a
 * parametric path whose tangent changes per cell, so a cart on it needs its
 * velocity projected onto a tangent read from somewhere: either a second grid
 * covering the world, or a per-cart polyline — which is the polygon layer
 * `rigidShapes.ts` exists to have deleted, re-entering through the back door.
 *
 * It also gives back both things the door was chosen to avoid. A cart banks on
 * a slope, so it rotates, so it re-bakes nothing but it *does* crawl its grain
 * (snag 2), and it needs the rail carved by `worldgen.ts` to be anywhere at
 * all.
 *
 * And the cap kills it independently of any of that. `MAX_AWAKE` is **4**, an
 * upload cap: a body that is moving dirties render tiles at two positions.
 * A crate is awake for the second it falls and then sleeps forever; a door is
 * awake for the seven ticks it swings. **A minecart's entire purpose is to be
 * in motion while the player watches it**, so one cart holds a quarter of the
 * frame's body-upload budget for as long as it exists, and a cart plus the
 * three crates in the room is the budget. Doors and crates fit inside four
 * because four is a *concurrency* limit on transient motion; a vehicle is not
 * transient. Raising the cap is the thing #42 wrote the arithmetic down
 * specifically to stop happening by accident.
 *
 * ==== WHAT THIS LANDING DELIBERATELY DOES NOT DO ========================
 *
 * **Placement is not here either, and that is a layering decision rather than
 * an omission (#84).** This file is the solver: it knows how a body falls, not
 * where one belongs. `world/bodySites.ts` owns where — which chunk draws a
 * crate, which of those are currently instantiated, and what the run remembers
 * about the ones that are not. The split is the same one `sim/supports.ts` and
 * `world/supportSites.ts` already make, and for the same reason.
 *
 * What the solver owes that layer is `capture`/`restore`/`retire` below: a body
 * has a mutable position, a rotation and a pixel array that has been burned and
 * dug since it was placed, so it cannot be re-derived from the seed the way a
 * support group can. It can be lifted out whole and put back whole, and those
 * three methods are that.
 *
 * **Body-versus-body contact is resolved at the level of the two bodies' tight
 * rotated AABBs**, not per pixel, because all four awake bodies are out of the
 * grid at the same moment and cannot see each other's cells. Body-versus-world
 * is per pixel, which is where all the interesting geometry is, and a
 * *sleeping* body is per-pixel to everything because it is simply terrain.
 *
 * That paragraph described an intent this file did not implement until the
 * audit pass: `fitsOtherBodies` skipped every body whose cells were out of the
 * grid, which at the moment it is called is every awake body there is. Two
 * crates converging in mid-air interpenetrated and deleted twenty-two cells of
 * wood. See the note on that function; the fixture is
 * `tests/rigid.test.ts` → "two crates converging in mid-air".
 *
 * Bodies do not push entities. They only crush them.
 */

import { profiler } from '../core/profiler';
import { Category, Flag, M, VOID } from './materials';
import { CellWorld } from './world';
import {
  GRAIN_ABSENT,
  MAX_EXTENT,
  MAX_SHAPE_CELLS,
  ORIENTS,
  shapes,
  type BakedShape,
} from './rigidShapes';

/**
 * Half-width of a body's **grain window**, in cells. `MAX_EXTENT`, so one span
 * fits every shape the bake will accept.
 */
export const GRAIN_HALF = MAX_EXTENT;

/** Edge of the square grain window. 25 cells; 625 bytes per body. */
export const GRAIN_SPAN = GRAIN_HALF * 2 + 1;

/**
 * One placed body's render grain, in world-aligned cells — issue #121.
 *
 * **What this exists to carry, and why it is shaped like this.** #42 snag 2
 * says the grain-crawl fix "means the renderer knowing which cells belong to
 * which body, which is the ownership map turned into a per-frame read — the
 * exact thing snag 1 says not to do". That is true of the map, and the map is
 * not what the renderer needs. It needs a *coordinate space*, and a body's
 * placement already fixes one: `place()` walks the mask anyway, so it can
 * stamp each cell's grain into a small window as it goes, for the cost of one
 * store per pixel in a loop that was already running.
 *
 * So the ownership read is not per frame and not per cell. It is per
 * **placement**, which is where the ownership actually changes, and a body
 * that is not moving does not re-derive it — a sleeping crate's window is
 * written once and then holds still, which is what lets the renderer skip the
 * upload entirely (`version`).
 *
 * `ox`/`oy` are the world cell of `data[0]`, so the renderer's lookup is a
 * subtraction and a bounds check with no trigonometry anywhere: the rotation
 * was already applied when the mask was baked.
 */
export interface BodyGrain {
  readonly id: number;
  /** World cell of `data[0]`. */
  ox: number;
  oy: number;
  /**
   * Bumped on every `place`. The renderer re-uploads a slot only when this
   * moves, so a body that is asleep — or awake but resting — costs zero bytes.
   */
  version: number;
  /** `GRAIN_SPAN²` bytes, row-major. `GRAIN_ABSENT` where no pixel lives. */
  readonly data: Uint8Array;
}

/**
 * What `render/terrain.ts` needs from the solver, and the whole of it.
 *
 * An interface rather than the class so the renderer does not import `sim/`'s
 * largest file to read 625 bytes, and so a test can supply a window without
 * standing up a solver.
 */
export interface GrainSource {
  /** Every **placed** body, in registration order. Reused; do not retain. */
  grainWindows(): readonly BodyGrain[];
}

/**
 * Awake bodies, ever.
 *
 * An upload cap, not a physics cap — see the module note. Four bodies dirtying
 * 2×2 render tiles at two positions each is 8,192 cells per frame, half the
 * 16,384 that blew the upload budget before, and eight bodies would be all of
 * it. The factor of two is deliberate.
 */
export const MAX_AWAKE = 4;

/** Registered bodies, awake or not. Sleeping ones are free; this is memory. */
export const MAX_BODIES = 32;

/** Cells a body may travel per axis per tick. `MAX_SPEED / 60`, exactly. */
export const MAX_STEP = 4;

/** Cells/sec. `MAX_STEP × 60`; the two must move together. */
export const MAX_SPEED = MAX_STEP * 60;

/** Cells/sec². The same figure `particles.ts` and the entity mover use. */
const GRAVITY = 1200;

/** Ticks a body must be still and supported before it sleeps. */
export const SLEEP_AFTER = 12;

/** Below this, a velocity is zero. Cells/sec. */
const SLEEP_SPEED = 1.5;

/** Below this, an angular velocity is zero. Orientation steps/sec. */
const SLEEP_SPIN = 0.4;

/**
 * Live pixels below this fraction of the original and the body comes apart.
 *
 * A crate that has lost 40% of its wall is not a crate. The remainder is
 * released into the grid as ordinary matter with `Flag.STATIC` cleared, so
 * whatever was going to happen to it — falling, burning down to ash — happens
 * through `cellsim` and `thermal` rather than through a bespoke debris path.
 */
export const FAIL_FRACTION = 0.6;

/** Ground friction, per second, applied to a supported body. */
const FRICTION = 6;

/** Angular damping, per second. */
const SPIN_DAMP = 3;

/**
 * How hard an off-centre landing tips a body. Steps/sec per cell of offset.
 *
 * **As calibrated, no body ever changes orientation under its own dynamics,
 * and the margin is 4%.** Worth writing down because it is not obvious from
 * either constant on its own. An impact grants
 * `omega = offset × (impact/60) × TIP_GAIN`; `offset` is a *mean* over the
 * contacting cells, so its ceiling is 6 for a 13-wide crate (every contact in
 * one corner column) and `impact` is capped at `MAX_SPEED`, giving
 * `6 × 4 × 0.06 = 1.44` steps/sec. `SPIN_DAMP` is 3/sec, so the total angle
 * that spin can ever integrate to is `1.44 / 3 = 0.48` — and `wantOrient` is
 * `Math.round(angle)`, which needs **0.5**. A hard, maximally off-centre
 * landing measures 0.312.
 *
 * So the 32 baked masks are reached only through `spawn(shape, x, y, awake,
 * orient)` and `restore`, never through play. That is not a defect and it is
 * not a reason to raise the gain — the tipping term still does the thing it
 * was added for, which is to stop a crate sliding off a ledge perfectly level
 * like a lift, and a crate that could flip would also be a crate whose grain
 * crawls (snag 2). It is recorded so that the next person to read
 * `fitsAll`, or to wonder why `tests/rigid.test.ts` only ever sees orientation
 * 0 outside the bake tests, does not have to rediscover the arithmetic.
 */
const TIP_GAIN = 0.06;

/**
 * Cells a door travels along its axis per tick.
 *
 * An integer, not a speed integrated over `dt`, and that is the difference
 * between a door and a crate. A crate's position is the output of a physical
 * integration and is fractional between grid poses; a door is a **machine**
 * whose two rest states are cells and whose travel is a whole number of them,
 * so driving it in whole cells means it arrives *exactly* at its stop rather
 * than within a rounding of it, and `at === goal` is an integer comparison
 * instead of an epsilon. It must not exceed `MAX_STEP`, which bounds the
 * per-axis probe loop the quota is derived from.
 */
export const DOOR_STEP = 2;

/**
 * Cells/sec. `DOOR_STEP × 60`, and **deliberately above `CRUSH_SPEED`.**
 *
 * A door that closed below the crush threshold would be a door you could stand
 * in with impunity, which makes the shut state cosmetic. Two cells a tick also
 * puts the crate's 13-cell travel at seven ticks, which reads as a door rather
 * than as a lift.
 */
export const DOOR_SPEED = DOOR_STEP * 60;

/** Minimum speed at which a body crushes anything. Cells/sec. */
export const CRUSH_SPEED = 60;

/** Damage per live pixel per cell of travel in the impact tick. */
const CRUSH_PER_PIXEL_CELL = 0.15;

/** Direction indices into a mask's `edge` lists. */
const DIR_RIGHT = 0;
const DIR_LEFT = 1;
const DIR_DOWN = 2;
const DIR_UP = 3;

/**
 * How the crush rule reaches the entity layer.
 *
 * An interface rather than a direct `World` import, because the asymmetry in
 * docs/01 §5.1 is a rule about *who* is hit, and a rule is only testable if the
 * two sides of it can be supplied identically. `tests/rigid.test.ts` runs one
 * fixture twice, changing nothing but what `isPlayer` answers.
 */
export interface CrushSink {
  /** Append the indices of entities overlapping the world-cell AABB to `out`. */
  query(x0: number, y0: number, x1: number, y1: number, out: number[]): void;
  isPlayer(idx: number): boolean;
  damage(idx: number, amount: number, bodyId: number): void;
}

/**
 * The single-axis constraint that makes a body a door.
 *
 * Everything a door is, as data: a segment in the grid, and which end is which.
 * There is no separate "state" field — the door's state *is* its position on
 * the segment, so the two cannot disagree, and a door restored from the ledger
 * has no state to restore beyond a pose it can re-derive in seven ticks.
 */
export interface DoorAxis {
  /** Unit step from the shut pose toward the open one. Exactly one is ±1. */
  dx: number;
  dy: number;
  /** Cells from shut to open. */
  travel: number;
  /**
   * Origin cell of the **shut** pose.
   *
   * The doorway is measured from here rather than from the body, because the
   * question a door asks the entity layer is "is anything in the doorway",
   * and a door that had already opened would otherwise be asking about the
   * recess it had slid into and would close on whoever was in the hole.
   */
  shutX: number;
  shutY: number;
  /**
   * Offset from the shut pose to the volume that holds the door open.
   *
   * **The doorway is not the door**, and the difference is the whole reason
   * this is data rather than a one-cell inflation of the plate's own box. The
   * sensor and the crush query are the same call (see `doorwayBusy`), so if the
   * sensed volume were the plate's own footprint then *anything a door could
   * crush would already be holding it open* and the crush branch would be
   * unreachable — the §5.1 asymmetry would be a rule about a thing that cannot
   * happen. Displacing the sensor perpendicular to travel by the plate's own
   * depth separates the two: standing **on** a hatch holds it open, falling
   * **through** the hole does not, and the plate sweeps back across you.
   *
   * It is also the honest description of a pressure plate: what opens it is
   * weight on top, not presence inside.
   */
  senseDx: number;
  senseDy: number;
}

/**
 * A body, lifted out of the registry whole — see `RigidBodies.capture`.
 *
 * The unit `world/bodySites.ts` stores in the run and hands back when the
 * chunk comes home, and the unit the save file encodes. One representation for
 * both, deliberately: an eviction round trip and a save/load round trip that
 * went through different code would agree in the suite and disagree in play.
 */
export interface BodyCapture {
  shape: number;
  /** Origin cell, rounded — a captured body is not mid-flight, it is at rest. */
  x: number;
  y: number;
  orient: number;
  /** Bit `p` set means canonical pixel `p` is still there. */
  alive: Uint32Array;
}

interface Body {
  id: number;
  shape: number;
  /** Origin, in world cells. Fractional between grid poses. */
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Orientation in 1/32-turn units, fractional. `orient` is its rounding. */
  angle: number;
  /** 1/32 turns per second. */
  omega: number;
  orient: number;

  awake: boolean;
  restTicks: number;
  dead: boolean;

  /** Non-null for a door: the segment its position is clamped to. */
  door: DoorAxis | null;

  /** The pose the body's pixels currently occupy in the grid. */
  placedX: number;
  placedY: number;
  placedOrient: number;
  placed: boolean;
  /** Set on spawn: the body owes the grid a first write even if it is asleep. */
  needsPlace: boolean;

  /** Per-pixel cell words. `mat[i] === VOID` means the pixel is gone. */
  mat: Uint16Array;
  temp: Int16Array;
  flags: Uint16Array;
  aux: Uint16Array;
  /** Live pixels — the body's mass, and the numerator of `FAIL_FRACTION`. */
  live: number;

  /**
   * Which mask entries the last `place` actually wrote, indexed by entry.
   *
   * Not bookkeeping for its own sake. A pixel whose target cell was occupied by
   * something the body could not displace is **not written**, and if `lift`
   * then walked the mask blindly it would read that foreign cell, decide it was
   * the body's, absorb it and clear it — a crate that eats the rock it is
   * pressed against. Lift is the exact inverse of place or it is a matter leak.
   */
  wrote: Uint8Array;

  /**
   * The body's render grain in world-aligned cells, and its change counter.
   *
   * Filled by `place`, read by `render/terrain.ts` through `grainWindows`.
   * See `BodyGrain`.
   */
  grain: BodyGrain;

  /**
   * Live pixels currently carrying `Flag.BURNING`, counted during `lift`.
   *
   * A body that is on fire must not sleep, and this is what makes that free:
   * `lift` is already reading every cell's flags, so the count costs one
   * increment. Without it a crate could catch, settle after twelve quiet ticks
   * and then burn to ash in the grid with the body none the wiser, waking only
   * when something else disturbed it.
   */
  burning: number;

  /**
   * The body was grounded and completely still at the end of the last tick.
   *
   * A resting body is **not lifted**, only read. That is not an optimisation
   * bolted on afterwards, it is a correctness fix: a body lifted every tick
   * leaves a body-shaped hole for `cellSim` to pour into, and write-back then
   * has to displace whatever poured in. A crate standing in a pool therefore
   * promoted its own volume of water into free particles *every tick*, and
   * `Particles.land` cannot always find a home for that many at once in a
   * packed pool, so the pool drained. Leaving a still body's `Flag.STATIC`
   * cells exactly where they are means nothing pours in and nothing has to be
   * displaced — and it also means a still body uploads nothing.
   */
  resting: boolean;
}

/** Pack a world cell into one integer key. ±1,048,575 cells per axis. */
function packCoord(x: number, y: number): number {
  return (x + 1048576) * 2097152 + (y + 1048576);
}

/**
 * Work units the most expensive shape can charge in its most expensive tick.
 *
 * Walks every baked orientation of every shipped shape, so adding a shape moves
 * the quota by exactly what the shape costs and nothing else. The bake's caps
 * bound the result — this can never exceed
 * `2·MAX_SHAPE_CELLS + 2·MAX_STEP·MAX_EDGE_CELLS + 2·MAX_EDGE_CELLS +
 *  MAX_BOUNDARY_CELLS + MAX_SHAPE_CELLS + 4·MAX_EDGE_CELLS` — and
 * `tests/rigid.test.ts` asserts both the value and that ceiling.
 */
export function worstBodyCost(): number {
  let worst = 0;
  for (const s of shapes()) {
    for (let k = 0; k < ORIENTS; k++) {
      const m = s.masks[k];
      let edge = 0;
      for (let d = 0; d < 4; d++) if (m.edge[d].length > edge) edge = m.edge[d].length;
      const cost =
        2 * m.dx.length +
        2 * MAX_STEP * edge +
        2 * edge +
        m.boundary.length +
        (m.dx.length + 4 * edge);
      if (cost > worst) worst = cost;
    }
  }
  return worst;
}

export class RigidBodies implements GrainSource {
  private readonly world: CellWorld;
  private readonly bodies: Body[] = [];
  private nextId = 1;

  /**
   * Which sleeping body owns a cell, plus a one-cell halo around each body.
   *
   * Snag 1 in the module note: there is no spare flag bit, so ownership lives
   * here. **Read from exactly one place** — `noteCellDamaged`, which
   * `CellWorld.damageCell` calls. The halo is what makes digging the floor out
   * from under a settled crate wake it: the damaged cell is not the crate's,
   * but it is next to it.
   */
  private readonly sleepingCells = new Map<number, number>();

  /** Scratch for `CrushSink.query`. Reused; this class allocates nothing. */
  private readonly victims: number[] = [];

  /** Scratch for `grainWindows`. Reused for the same reason as `victims`. */
  private readonly grainOut: BodyGrain[] = [];

  /**
   * The docs/01 §9 quota, derived — from the **shipped shapes**, not from the
   * bake's validity caps.
   *
   *   per body   lift + write-back        2 × cells
   *              axis probes              2 × MAX_STEP × edge
   *              ground + tip probes      2 × edge
   *              rotation probe           1 × boundary
   *              sleep registration       cells + 4 × edge
   *   × MAX_AWAKE
   *
   * where `cells`, `edge` and `boundary` are the worst orientation of the
   * worst shape actually baked. For the crate that is 75, 41 and 75 — a crate
   * at 45°, where a one-cell wall thickens under the inverse map — giving 874
   * per body and **3496**; the sleeping-door poll below adds 32 more, for
   * **3528** in total.
   *
   * Deriving from the caps instead would give 9356, and that difference is not
   * cosmetic: docs/01 §9's convention is that a line item is `quota × measured
   * cost per unit`, so a quota five times larger than anything reachable would
   * have booked five times the milliseconds and eaten the whole 0.5 ms of
   * frame slack for work no shipped shape can ask for. `MAX_SHAPE_CELLS` and
   * friends stay as the ceiling `bakeShape` refuses to exceed, which is what
   * keeps this figure bounded when a shape is added; they are not the budget.
   *
   * The sleep-registration term is a body's most expensive tick and happens
   * once per settle; it is in the quota because a quota bounds the worst tick,
   * not the usual one. A settled crate's steady-state cost is **zero** — it is
   * not in the awake set at all.
   *
   * **Plus `MAX_BODIES` for the sleeping-door poll.** That is the one thing in
   * this file a body outside the awake set can cost, and it is one unit: a
   * single `CrushSink.query` against a fixed rectangle (`pollDoor`). A
   * registry entirely full of doors therefore adds 32 units to the 3496-unit
   * per-body total — 0.9%, which does not move the §9 line — and a settled *crate*
   * still costs exactly zero, which is the claim `tests/rigid.test.ts` asserts
   * directly. Charging it is not pedantry: a pass with a cost and no line is
   * how #75's two unmeasured systems got there.
   */
  readonly maxWorkPerTick: number;

  /** Units spent this tick. Telemetry, and the thing under the gate. */
  workThisTick = 0;

  /** Bodies currently in the active set. Never above `MAX_AWAKE`. */
  awakeCount = 0;

  /**
   * Wake requests refused because all four slots were taken.
   *
   * Reported rather than thrown, and asserted in the suite, so the cap being
   * hit in normal play is visible instead of being a mystery about crates that
   * do not fall.
   */
  wakesRefused = 0;

  /** Bodies that came apart this tick, and where. Drives the ticker. */
  onBreak: ((bodyId: number, x: number, y: number) => void) | null = null;

  /**
   * Player crushes declined by the docs/01 §5.1 asymmetry.
   *
   * A counter rather than nothing, because "the player took no damage" is also
   * what an empty query looks like. The asymmetry test asserts this is 1 in the
   * player fixture, which a fixture that simply failed to overlap cannot fake.
   */
  playerCrushesDeclined = 0;

  crush: CrushSink | null = null;

  /**
   * Where matter the body lands on top of goes.
   *
   * `Particles.promote` clears the source cell, so this **moves** matter rather
   * than duplicating it. Wired by the game; a rig without it simply refuses to
   * place a pixel over an occupied cell, which is also correct, just less
   * convincing.
   */
  displaceTo: { promote(x: number, y: number, vx: number, vy: number): boolean } | null = null;

  constructor(world: CellWorld) {
    this.world = world;
    this.maxWorkPerTick = MAX_AWAKE * worstBodyCost() + MAX_BODIES;
  }

  get bodyCount(): number {
    return this.bodies.length;
  }

  /**
   * `GrainSource`: the grain window of every body whose pixels are in the
   * grid — issue #121.
   *
   * Only **placed** bodies appear, and that is the correctness condition
   * rather than a filter for cheapness. A body mid-lift has no cells in the
   * world, so a window claiming its old pose would recolour whatever poured
   * into the hole. `placed` is exactly "the window describes the grid".
   *
   * Awake and asleep are *not* distinguished, and must not be. Body-local
   * grain for a moving crate and world-hashed grain for a still one would make
   * the speckle jump at the moment it settles — the crawl replaced by a pop,
   * on the frame the player is most likely to be looking at it.
   */
  grainWindows(): readonly BodyGrain[] {
    const out = this.grainOut;
    out.length = 0;
    for (let i = 0; i < this.bodies.length; i++) {
      const b = this.bodies[i];
      if (b.placed) out.push(b.grain);
    }
    return out;
  }

  /** Ids in registry order. Registry order is id order. */
  bodyIds(): number[] {
    const out: number[] = [];
    for (let i = 0; i < this.bodies.length; i++) out.push(this.bodies[i].id);
    return out;
  }

  /**
   * The full state of one body, for determinism fixtures and for the debug
   * overlay. Allocates — not for use inside the tick.
   */
  snapshot(id: number): string | null {
    const b = this.find(id);
    if (b === null) return null;
    return (
      `${b.id}:${b.shape}:${b.x}:${b.y}:${b.vx}:${b.vy}:${b.angle}:${b.omega}:` +
      `${b.orient}:${b.awake ? 1 : 0}:${b.resting ? 1 : 0}:${b.restTicks}:${b.live}:` +
      `${b.placedX},${b.placedY},${b.placedOrient}`
    );
  }

  /** Every body's state, joined. The determinism comparison. */
  snapshotAll(): string {
    const out: string[] = [];
    for (let i = 0; i < this.bodies.length; i++) {
      out.push(this.snapshot(this.bodies[i].id) as string);
    }
    return out.join('|');
  }

  isAwake(id: number): boolean {
    const b = this.find(id);
    return b !== null && b.awake;
  }

  livePixels(id: number): number {
    const b = this.find(id);
    return b === null ? 0 : b.live;
  }

  position(id: number): { x: number; y: number; orient: number } | null {
    const b = this.find(id);
    return b === null ? null : { x: b.x, y: b.y, orient: b.orient };
  }

  private find(id: number): Body | null {
    for (let i = 0; i < this.bodies.length; i++) {
      if (this.bodies[i].id === id) return this.bodies[i];
    }
    return null;
  }

  clear(): void {
    this.bodies.length = 0;
    this.sleepingCells.clear();
    this.awakeCount = 0;
    this.wakesRefused = 0;
    this.workThisTick = 0;
    this.nextId = 1;
  }

  // ------------------------------------------------------------- lifecycle

  /**
   * Register a body at a world cell. Returns its id, or -1 if the registry is
   * full. The body is placed into the grid by the next `writeBack`.
   */
  spawn(shape: number, x: number, y: number, awake = true, orient = 0): number {
    if (this.bodies.length >= MAX_BODIES) return -1;
    const s = shapes()[shape];
    const id = this.nextId++;
    const o = ((orient % ORIENTS) + ORIENTS) % ORIENTS;
    const b: Body = {
      id,
      shape,
      x,
      y,
      vx: 0,
      vy: 0,
      // `angle` seeds to the orientation, not to zero: a crate placed at 45°
      // must stay at 45°, and `wantOrient` is a rounding of `angle`.
      angle: o,
      omega: 0,
      orient: o,
      awake: false,
      restTicks: 0,
      dead: false,
      door: null,
      placedX: 0,
      placedY: 0,
      placedOrient: 0,
      placed: false,
      needsPlace: true,
      mat: Uint16Array.from(s.mat),
      temp: new Int16Array(s.pixelCount),
      flags: new Uint16Array(s.pixelCount),
      aux: new Uint16Array(s.pixelCount),
      live: s.pixelCount,
      wrote: new Uint8Array(MAX_SHAPE_CELLS),
      grain: { id, ox: 0, oy: 0, version: 0, data: new Uint8Array(GRAIN_SPAN * GRAIN_SPAN) },
      burning: 0,
      resting: false,
    };
    for (let i = 0; i < s.pixelCount; i++) {
      b.temp[i] = M.spawnTemp[s.mat[i]];
      b.aux[i] = M.burnDuration[s.mat[i]];
    }
    this.bodies.push(b);
    if (awake) this.wake(id);
    return id;
  }

  /**
   * Register a door: a body at `(x, y)` whose position is clamped to the
   * segment running `travel` cells in direction `(dx, dy)` from there.
   *
   * `(x, y)` is the **shut** pose, always, and a door is registered **asleep**,
   * always. Both are the same decision: a door with nobody near it is shut, so
   * that is the state the seed authors and the state the ledger restores, and a
   * door that woke on registration would spend one of the four upload slots
   * driving itself to a rest it is already in. It wakes from `pollDoors`, from
   * `damageCell`, or from ignition — the three things that can mean something
   * has happened at a doorway.
   *
   * Refuses a diagonal or zero axis rather than accepting one: every probe
   * below is one of the four baked `edge` lists, and there is no fifth.
   */
  spawnDoor(
    shape: number, x: number, y: number,
    dx: number, dy: number, travel: number,
    senseDx = 0, senseDy = 0,
  ): number {
    if (Math.abs(dx) + Math.abs(dy) !== 1 || travel <= 0) return -1;
    const id = this.spawn(shape, x, y, false, 0);
    if (id < 0) return -1;
    const b = this.find(id) as Body;
    b.door = { dx, dy, travel, shutX: x, shutY: y, senseDx, senseDy };
    return id;
  }

  /** The axis a door was registered on, or null. Test seam and debug overlay. */
  doorAxis(id: number): DoorAxis | null {
    const b = this.find(id);
    return b === null ? null : b.door;
  }

  /**
   * How far a door has travelled from shut, in cells. `-1` if it is not a door.
   *
   * Derived from the pose rather than stored, so "the door is open" and "the
   * door's pixels are over there" cannot disagree.
   */
  doorOffset(id: number): number {
    const b = this.find(id);
    if (b === null || b.door === null) return -1;
    return this.offsetOf(b, b.door);
  }

  private offsetOf(b: Body, d: DoorAxis): number {
    return (Math.round(b.x) - d.shutX) * d.dx + (Math.round(b.y) - d.shutY) * d.dy;
  }

  /**
   * Everything about a body that is not re-derivable from `(shape, seed)`.
   *
   * Pose plus a liveness bit per canonical pixel, and deliberately nothing
   * else. Temperature, burn fuel and `Flag.BURNING` are *cell* state and are
   * left behind on purpose: they are exactly what docs/01 §8 says a run does
   * not carry, and a crate that came back still alight after its chunk had been
   * evicted would be remembering a fire the rest of the world had forgotten.
   * What survives is the shape of the damage, which is the thing the player did.
   *
   * **A door is captured at its shut pose, wherever it actually is.** Its pose
   * is not something the player did — it is something the door decided, from a
   * sensor reading that will be taken again the moment anyone comes back, and
   * it converges in seven ticks. Recording it would put a row in the save for
   * every door the player merely walked over, and would restore a half-open
   * hatch into a room whose reason for being half open had gone home. What the
   * player did to a door is the damage, and that is what is kept.
   */
  capture(id: number): BodyCapture | null {
    const b = this.find(id);
    if (b === null || b.dead) return null;
    const n = shapes()[b.shape].pixelCount;
    const alive = new Uint32Array((n + 31) >> 5);
    for (let p = 0; p < n; p++) {
      if (b.mat[p] !== VOID) alive[p >> 5] |= 1 << (p & 31);
    }
    const x = b.door !== null ? b.door.shutX : Math.round(b.x);
    const y = b.door !== null ? b.door.shutY : Math.round(b.y);
    return { shape: b.shape, x, y, orient: b.orient, alive };
  }

  /**
   * Register a body from a capture, asleep, at the captured pose.
   *
   * Asleep and not awake: a restored crate is a crate that was already settled
   * somewhere the player was not looking, and waking it would spend one of the
   * four upload slots to re-derive a rest it was already in. It wakes the moment
   * anything touches it or the cell beside it, like any other sleeping body.
   *
   * A capture whose liveness mask is already below `FAIL_FRACTION` is refused
   * rather than registered, because `step` would break it apart on its first
   * tick and drop a pile of planks into a chunk the player has just walked back
   * into. A body that failed is `gone` in the ledger, not restored and re-killed.
   */
  restore(cap: BodyCapture, door: DoorAxis | null = null): number {
    const s = shapes()[cap.shape];
    let live = 0;
    for (let p = 0; p < s.pixelCount; p++) {
      if ((cap.alive[p >> 5] & (1 << (p & 31))) !== 0) live++;
    }
    if (live === 0 || live < s.pixelCount * FAIL_FRACTION) return -1;
    // A door is restored shut and on its own axis, which the *site* supplies:
    // the segment is a fact about the doorway the seed carved, so it is
    // re-derived like the doorway rather than saved beside it.
    const id =
      door !== null
        ? this.spawnDoor(
            cap.shape, door.shutX, door.shutY,
            door.dx, door.dy, door.travel, door.senseDx, door.senseDy,
          )
        : this.spawn(cap.shape, cap.x, cap.y, false, cap.orient);
    if (id < 0) return -1;
    const b = this.find(id) as Body;
    for (let p = 0; p < s.pixelCount; p++) {
      if ((cap.alive[p >> 5] & (1 << (p & 31))) === 0) b.mat[p] = VOID;
    }
    b.live = live;
    return id;
  }

  /**
   * Take a body out of the registry without breaking it.
   *
   * The counterpart to `restore`, and the reason a body can leave memory
   * without leaving the run. **`clearCells` is not a convenience flag.** When
   * the body's chunk is still resident its pixels are sitting in the grid as
   * `Flag.STATIC` matter and have to be removed, or the crate leaves a
   * crate-shaped fossil nobody owns. When the chunk has already been evicted
   * they went with it, and clearing would call `writeRaw` — which generates the
   * chunk on demand — and carve the crate's silhouette out of freshly generated
   * rock. Passing the wrong one is a hole in the world either way.
   */
  retire(id: number, clearCells: boolean): boolean {
    const b = this.find(id);
    if (b === null) return false;
    if (clearCells) this.clearPlaced(b);
    else b.placed = false;
    this.unregisterSleeping(b);
    if (b.awake) {
      b.awake = false;
      this.awakeCount--;
    }
    b.dead = true;
    let w = 0;
    for (let i = 0; i < this.bodies.length; i++) {
      if (this.bodies[i] === b) continue;
      this.bodies[w++] = this.bodies[i];
    }
    this.bodies.length = w;
    return true;
  }

  /**
   * Put a body back in the active set. Refused, and counted, when all four
   * slots are taken — the cap is the upload budget and it does not negotiate.
   */
  wake(id: number): boolean {
    const b = this.find(id);
    if (b === null || b.dead || b.awake) return false;
    if (this.awakeCount >= MAX_AWAKE) {
      this.wakesRefused++;
      return false;
    }
    this.unregisterSleeping(b);
    b.awake = true;
    b.restTicks = 0;
    // A body that has just been woken has, by definition, had something happen
    // to it, so it does not get the resting shortcut on its first tick back.
    b.resting = false;
    this.awakeCount++;
    return true;
  }

  /**
   * The **only** reader of `sleepingCells`. Wired to two events, both rare:
   * `CellWorld.onCellDamaged` (fired inside `damageCell`) and
   * `Thermal.onIgnite` (fired once per cell that catches).
   *
   * Both have already landed in the grid by the time this runs, and that is the
   * point — a sleeping body's cells are ordinary matter to every other system,
   * so nothing here has to model damage or fire. Waking is enough: the next
   * `lift` reads the grid back into the body and the hole, or the flame, is
   * simply there.
   *
   * Ignition is on this hook and not only damage because a crate that catches
   * fire while settled must come apart when it has burned through. It costs one
   * `Map` lookup per newly-ignited cell, and none at all while nothing sleeps.
   */
  noteCellDisturbed(x: number, y: number): void {
    if (this.sleepingCells.size === 0) return;
    const id = this.sleepingCells.get(packCoord(x, y));
    if (id !== undefined) this.wake(id);
  }

  // ------------------------------------------------------------ the tick

  /**
   * Take every awake body out of the grid, absorbing whatever the world did to
   * its cells since the last write-back.
   *
   * Runs **before** `cellSim`, which is the only pass that must not see the
   * body: to `cellsim` a crate is a pile of wood, and `Flag.STATIC` is the only
   * thing stopping it from treating it as one. Lifting rather than trusting the
   * flag is what lets the body move at all.
   *
   * **A resting body is read but not cleared.** See `Body.resting`: leaving a
   * still body's cells in place is what stops a crate standing in a pool from
   * cycling the pool through the free-particle layer once per tick. Absorption
   * still happens, so a resting crate that catches fire still notices.
   */
  lift(): void {
    profiler.begin('rigid');
    this.workThisTick = 0;
    const w = this.world;

    for (let k = 0; k < this.bodies.length; k++) {
      const b = this.bodies[k];
      if (!b.awake || !b.placed) continue;
      const hold = b.resting;
      const mask = shapes()[b.shape].masks[b.placedOrient];
      const dx = mask.dx;
      const dy = mask.dy;
      const src = mask.src;
      let burning = 0;

      for (let i = 0; i < dx.length; i++) {
        this.workThisTick++;
        if (b.wrote[i] === 0) continue;
        if (!hold) b.wrote[i] = 0;
        const p = src[i];
        const wx = b.placedX + dx[i];
        const wy = b.placedY + dy[i];
        const m = w.peek(wx, wy);

        // A duplicate entry gives its cell back and reads nothing: the primary
        // owns this pixel's state. See `RotatedMask.primary`. This is checked
        // **before** the dead-pixel skip below, and that ordering is the whole
        // point: when the primary's cell has just been destroyed the pixel is
        // marked dead, and a duplicate that then short-circuited on "the pixel
        // is dead" would leave its own cell standing in the world as wood
        // nobody owns.
        if (mask.primary[i] === 0) {
          if (!hold && m !== VOID && M.category[m] === Category.RIGID) {
            w.writeRaw(wx, wy, VOID, w.getTemp(wx, wy), 0, 0);
          }
          continue;
        }
        if (b.mat[p] === VOID) continue;

        if (m === VOID) {
          // Dug out, burned away, or dissolved. The pixel is gone.
          b.mat[p] = VOID;
          b.live--;
          if (hold) b.wrote[i] = 0;
          continue;
        }
        if (M.category[m] !== Category.RIGID) {
          // Wood that finished burning is `ASH`, which is LOOSE. Loose matter
          // is not structure, so the body stops owning it and it stays exactly
          // where it is — as ordinary matter, with STATIC cleared so `cellsim`
          // takes it from here. This is the whole of "the crate chars".
          w.clearFlag(wx, wy, Flag.STATIC);
          b.mat[p] = VOID;
          b.live--;
          if (hold) b.wrote[i] = 0;
          continue;
        }

        // Absorb all four words. `aux` matters as much as `mat`: it is the
        // burn-fuel counter, and dropping it makes a burning crate immortal.
        b.mat[p] = m;
        b.temp[p] = w.getTemp(wx, wy);
        b.flags[p] = w.getFlags(wx, wy) & ~(Flag.MOVED | Flag.STATIC);
        b.aux[p] = w.getAux(wx, wy);
        if ((b.flags[p] & Flag.BURNING) !== 0) burning++;
        if (!hold) w.writeRaw(wx, wy, VOID, b.temp[p], 0, 0);
      }
      b.burning = burning;
      if (!hold) b.placed = false;
    }
    profiler.end('rigid');
  }

  /**
   * Integrate, collide, crush, and decide about sleep.
   *
   * Runs **after** `cellSim`, so the world the body collides against is this
   * tick's settled world rather than last tick's.
   */
  step(dt: number): void {
    profiler.begin('rigid');

    for (let k = 0; k < this.bodies.length; k++) {
      const b = this.bodies[k];
      if (b.dead) continue;
      if (!b.awake) {
        // The only thing a sleeping body is asked per tick, and only doors are
        // asked it. One `CrushSink.query` against a fixed rectangle — no cell
        // reads, no `sleepingCells` lookup (snag 1), nothing that scales with
        // the body's size. A sleeping crate is still asked nothing at all.
        if (b.door !== null) this.pollDoor(b);
        continue;
      }
      const shape = shapes()[b.shape];

      // ---- structural failure, checked before anything is integrated so a
      // body that is no longer a body never gets another tick of physics.
      // **This is also what stops a door finishing its swing as a corpse:** the
      // drive below is never reached by a body that failed this tick, and the
      // target it was driving to lives in `b.door`, which dies with it.
      if (b.live === 0 || b.live < shape.pixelCount * FAIL_FRACTION) {
        this.breakApart(b);
        continue;
      }

      if (b.door !== null) {
        this.stepDoor(b, shape);
        continue;
      }

      // ---- the resting fast path. A body that was still last tick still has
      // its cells in the grid (see `Body.resting`), so before anything else:
      // is it still still? If so there is nothing to integrate, nothing to
      // clear and nothing to write back — one probe and out.
      if (b.resting) {
        const stillDown = !this.fitsDir(
          b, Math.round(b.x), Math.round(b.y), b.orient, DIR_DOWN,
        );
        if (stillDown && b.vx === 0 && b.vy === 0 && b.omega === 0) {
          if (b.burning === 0) b.restTicks++;
          else b.restTicks = 0;
          continue;
        }
        // Something changed under it. Take it out of the grid — `cellSim` has
        // already run this tick, so this is the same removal `lift` would have
        // done, one pass later.
        this.clearPlaced(b);
        b.resting = false;
      }

      const beforeX = b.x;
      const beforeY = b.y;

      b.vy += GRAVITY * dt;
      const sp = Math.sqrt(b.vx * b.vx + b.vy * b.vy);
      if (sp > MAX_SPEED) {
        b.vx = (b.vx / sp) * MAX_SPEED;
        b.vy = (b.vy / sp) * MAX_SPEED;
      }

      let ix = Math.round(b.x);
      let iy = Math.round(b.y);

      // ---- X, then Y, resolved independently. The same trick the entity
      // mover uses, and for the same reason: a single swept test against a
      // corner sticks the body on the wall.
      const wantX = b.x + b.vx * dt;
      const targetX = Math.round(wantX);
      let blockedX = false;
      const stepX = targetX > ix ? 1 : -1;
      const nX = Math.min(MAX_STEP, Math.abs(targetX - ix));
      for (let n = 0; n < nX; n++) {
        if (!this.fitsDir(b, ix + stepX, iy, b.orient, stepX > 0 ? DIR_RIGHT : DIR_LEFT)) {
          blockedX = true;
          break;
        }
        ix += stepX;
      }
      if (blockedX) {
        b.x = ix;
        b.vx = 0;
      } else {
        b.x = Math.abs(targetX - Math.round(b.x)) > MAX_STEP ? ix : wantX;
      }

      const wantY = b.y + b.vy * dt;
      const targetY = Math.round(wantY);
      let blockedY = false;
      const stepY = targetY > iy ? 1 : -1;
      const nY = Math.min(MAX_STEP, Math.abs(targetY - iy));
      for (let n = 0; n < nY; n++) {
        if (!this.fitsDir(b, ix, iy + stepY, b.orient, stepY > 0 ? DIR_DOWN : DIR_UP)) {
          blockedY = true;
          break;
        }
        iy += stepY;
      }
      const impact = blockedY && stepY > 0 ? b.vy : 0;
      if (blockedY) {
        b.y = iy;
        b.vy = 0;
      } else {
        b.y = Math.abs(targetY - Math.round(b.y)) > MAX_STEP ? iy : wantY;
      }

      // ---- support, and the torque an off-centre one applies.
      const grounded = !this.fitsDir(b, ix, iy + 1, b.orient, DIR_DOWN);
      if (impact > 0) {
        // Where the ground actually is, relative to the body's origin. A crate
        // that lands with its left half over a ledge tips right, because the
        // contacts are all to the left of the centre. This is the entire
        // rotation model, and it is the reason a crate does not slide off a
        // ledge perfectly level like a lift.
        const offset = this.contactOffset(b, ix, iy, b.orient);
        b.omega += -offset * (impact / 60) * TIP_GAIN;
      }
      if (grounded) {
        const f = Math.max(0, 1 - FRICTION * dt);
        b.vx *= f;
        // Gravity must not accumulate into the ground. Without this the body
        // gains 20 cells/s every tick it rests, spends it on a sub-cell
        // descent, is blocked, and zeroes it again — an oscillation in which
        // `vy` is never 0 at the moment sleep is decided, so a crate on a floor
        // never settles. It cost an afternoon and it looked like a sleep bug.
        if (b.vy > 0) {
          b.vy = 0;
          b.y = iy;
        }
      }

      // ---- rotation. Quantised to the 32 baked orientations; a rotation the
      // world will not accept is simply not taken, and costs the spin.
      b.angle += b.omega * dt;
      b.omega *= Math.max(0, 1 - SPIN_DAMP * dt);
      const wantOrient = ((Math.round(b.angle) % ORIENTS) + ORIENTS) % ORIENTS;
      if (wantOrient !== b.orient) {
        if (this.fitsAll(b, ix, iy, wantOrient)) {
          b.orient = wantOrient;
        } else {
          b.omega = 0;
          b.angle = b.orient;
        }
      }

      if (Math.abs(b.vx) < SLEEP_SPEED) b.vx = 0;
      if (Math.abs(b.vy) < SLEEP_SPEED) b.vy = 0;
      if (Math.abs(b.omega) < SLEEP_SPIN) b.omega = 0;

      // ---- crush, before sleep so an impact tick still hits.
      const moved = Math.sqrt(
        (b.x - beforeX) * (b.x - beforeX) + (b.y - beforeY) * (b.y - beforeY),
      );
      if (this.crush !== null && moved * 60 >= CRUSH_SPEED) {
        this.applyCrush(b, shape, moved);
      }

      // A burning body never sleeps: sleeping means never lifting again, and a
      // body that never lifts never notices that its wood has become ash.
      const still = grounded && b.vx === 0 && b.vy === 0 && b.omega === 0;
      if (still && b.burning === 0) b.restTicks++;
      else b.restTicks = 0;
      // A burning body is excluded from the resting fast path as well as from
      // sleep, so its cells make the full round trip every tick while they are
      // changing — fuel down, wood to ash.
      //
      // **Unlike the sleep exclusion above, no fixture can tell the difference,
      // and the reason this comment used to give was wrong.** It said the round
      // trip is what carries `aux` back into the grid so the fuel is not
      // re-seeded; in fact a *resting* body is never written back at all, so
      // there is nothing to re-seed it, and `lift` still absorbs all four words
      // with `hold` set. Deleting `&& b.burning === 0` here leaves the whole
      // suite green: the crate still refuses to sleep (the resting branch in
      // `step` zeroes `restTicks` while `b.burning` is nonzero), still chars,
      // still fails. It is kept because a body whose cells are being rewritten
      // by another system every tick is not what "resting" is meant to describe,
      // and the fast path's correctness argument — nothing pours into a still
      // body's cells — is one about a body the world is *not* changing. That is
      // a design boundary rather than an observable, and it is labelled as one.
      b.resting = still && b.burning === 0;
    }

    // ---- compact. A broken body leaves the registry the tick it breaks.
    let w = 0;
    for (let i = 0; i < this.bodies.length; i++) {
      if (this.bodies[i].dead) continue;
      this.bodies[w++] = this.bodies[i];
    }
    this.bodies.length = w;

    profiler.end('rigid');
  }

  // ----------------------------------------------------------------- doors

  /**
   * Is anything standing in this door's doorway?
   *
   * The rectangle is the **shut** pose's tight AABB, displaced by
   * `senseDx/senseDy` and inflated by one cell. It does not move with the door:
   * the thing being sensed is the doorway, and a door that asked about its own
   * current cells would, once open, be asking about the recess it had slid
   * into — and would then shut on whoever had fallen through.
   *
   * This is `CrushSink.query` and not a sensor interface of its own, which
   * buys the invariant that nothing can hold a door open that the door cannot
   * also see. A rig with no crush sink has doors that never open, which is the
   * same failure mode as a rig with no `displaceTo`: correct, just inert.
   */
  private doorwayBusy(b: Body, d: DoorAxis): boolean {
    if (this.crush === null) return false;
    const m = shapes()[b.shape].masks[0];
    const cx = d.shutX + d.senseDx;
    const cy = d.shutY + d.senseDy;
    const out = this.victims;
    out.length = 0;
    this.crush.query(cx + m.minDx - 1, cy + m.minDy - 1, cx + m.maxDx + 1, cy + m.maxDy + 1, out);
    return out.length > 0;
  }

  /**
   * The one thing a sleeping door does. Costs a single work unit and one
   * entity query; a door nobody is standing at costs exactly that and nothing
   * else, and a door outside the ring is not registered at all.
   *
   * Waking here rather than opening here is deliberate: `step` runs after
   * `cellSim`, and a body that has been asleep has its cells in the grid as
   * ordinary `Flag.STATIC` matter. It has to make one full `lift` before it can
   * move, so the door opens on the tick *after* the one it is stood on. That
   * tick of latency is the price of sleeping bodies being terrain, and it is
   * the same tick a crate waits after you dig the floor from under it.
   */
  private pollDoor(b: Body): void {
    this.workThisTick++;
    const d = b.door as DoorAxis;
    // Only a *shut* sleeping door is opened by contact. One that fell asleep
    // part-open did so because it was jammed there, and standing in it is not
    // new information — the thing that frees it is `damageCell`.
    if (this.offsetOf(b, d) !== 0) return;
    if (this.doorwayBusy(b, d)) this.wake(b.id);
  }

  /**
   * Drive a door along its axis toward whichever of its two rest states the
   * doorway is currently asking for.
   *
   * The whole constraint is three integers: where it is on the segment, where
   * it wants to be, and one of the four baked leading-edge directions. There is
   * no gravity term, no rotation term and no `dt` — see `DOOR_STEP`.
   */
  private stepDoor(b: Body, shape: BakedShape): void {
    const d = b.door as DoorAxis;
    const busy = this.doorwayBusy(b, d);
    this.workThisTick++;
    const goal = busy ? d.travel : 0;
    let at = this.offsetOf(b, d);

    const sign = goal > at ? 1 : goal < at ? -1 : 0;
    const sx = sign * d.dx;
    const sy = sign * d.dy;
    const dir = sx > 0 ? DIR_RIGHT : sx < 0 ? DIR_LEFT : sy > 0 ? DIR_DOWN : DIR_UP;
    let ix = Math.round(b.x);
    let iy = Math.round(b.y);

    // ---- the resting fast path, and it is worth more to a door than to a
    // crate: a door is at one of its two stops for almost all of its life, so
    // this is the steady state rather than the end state. A resting body keeps
    // its cells in the grid, so it is neither lifted nor written back nor
    // uploaded, and the only cost is the sensor query above.
    if (b.resting) {
      // **Probe before disturbing anything.** A jammed door asked for a pose it
      // cannot have; if it left the resting path to find that out it would
      // clear and re-place all sixty-five of its cells every tick — an upload
      // per tick, forever, for a plate that is not moving. `fitsDir` reads the
      // cells one step *outside* the new pose, so a still-placed door does not
      // probe against its own pixels.
      const free = sign !== 0 && this.fitsDir(b, ix + sx, iy + sy, b.orient, dir);
      if (!free) {
        // A door being held open is not at rest, whatever its velocity says.
        // Without this it would sleep under the player's feet after twelve
        // ticks, be re-woken by the next `pollDoor`, and cycle — paying a
        // register/unregister pair every thirteen ticks for standing still.
        // The same clause keeps a *jammed* door awake and retrying while
        // somebody is asking for it, and lets it sleep the moment they leave.
        if (b.burning === 0 && !busy) b.restTicks++;
        else b.restTicks = 0;
        return;
      }
      // The doorway changed its mind and the way is clear. Take the cells back
      // out of the grid — `cellSim` has already run, so this is the removal
      // `lift` would have done one pass earlier.
      this.clearPlaced(b);
      b.resting = false;
    }

    let stepped = 0;
    if (sign !== 0) {
      const n = Math.min(DOOR_STEP, sign > 0 ? goal - at : at - goal);
      for (let k = 0; k < n; k++) {
        // Jammed. Keep the target and retry next tick; nothing is pushed.
        if (!this.fitsDir(b, ix + sx, iy + sy, b.orient, dir)) break;
        ix += sx;
        iy += sy;
        stepped++;
      }
      b.x = ix;
      b.y = iy;
    }

    // The realised velocity, not the commanded one: a jammed door reports zero,
    // so it neither crushes nor throws a splash out of `place`. It exists for
    // those two readers only — a door's position comes from `stepped`, never
    // from integrating this.
    b.vx = stepped * sx * 60;
    b.vy = stepped * sy * 60;
    // A door does not turn, ever, and this is the line that says so rather
    // than an absence of code that might turn it. `snapshot` reads both, so a
    // divergence here is a determinism failure the fixture catches.
    b.omega = 0;
    b.angle = b.orient;

    if (this.crush !== null && stepped * 60 >= CRUSH_SPEED) this.applyCrush(b, shape, stepped);

    // A door that did not move this tick is still — including a jammed one,
    // which is the decision in the module note: a door stuck shut is a floor
    // and should cost what a floor costs. `damageCell` wakes it when whatever
    // jammed it is dug out.
    const still = stepped === 0;
    if (still && b.burning === 0 && !busy) b.restTicks++;
    else b.restTicks = 0;
    b.resting = still && b.burning === 0;
  }

  /**
   * Put every body's pixels back into the grid.
   *
   * Runs **before** the material passes, which is the improvement on the talk's
   * frame order: `thermal`, `reactions` and the rest see WOOD cells at the
   * crate's real position and treat them as wood, with no knowledge that a
   * rigid body exists. A linear walk over the baked mask — no trigonometry, no
   * allocation, one `writeRaw` per live pixel.
   */
  writeBack(): void {
    profiler.begin('rigid');
    for (let k = 0; k < this.bodies.length; k++) {
      const b = this.bodies[k];
      if (b.dead) continue;
      if (!b.awake && !b.needsPlace) continue;

      // A body still holding its cells (the resting fast path) has nothing to
      // write: the grid already *is* the body, right down to the flags it
      // absorbed this tick. Skipping the write is the whole upload saving — a
      // still crate dirties no render tile at all.
      if (!b.placed) this.place(b, Math.round(b.x), Math.round(b.y), b.orient);
      const seeded = b.needsPlace;
      b.needsPlace = false;

      // A body that arrived asleep — placed by worldgen, or restored from the
      // run's ledger — has never been through the settle path below, so nothing
      // has ever indexed its cells. Without this it is a body whose pixels are
      // in the grid and whose id is in no map: digging the floor out from under
      // it does nothing, breaking it does nothing, and it sits there as
      // indestructible STATIC wood forever. The whole of #84's placement runs
      // through this branch, and none of #42's fixtures could reach it, because
      // every one of them spawned awake.
      if (!b.awake && seeded) this.registerSleeping(b);

      if (b.awake && b.restTicks >= SLEEP_AFTER) {
        // Sleeping is a *removal*, not a mode: out of the active set, cells
        // left in the grid as STATIC, ownership recorded for `damageCell`.
        b.awake = false;
        b.restTicks = 0;
        this.awakeCount--;
        this.registerSleeping(b);
      }
    }
    profiler.end('rigid');
  }

  // ------------------------------------------------------------- internals

  /**
   * Remove a body's cells from the grid mid-tick.
   *
   * The resting fast path's escape hatch: a still body kept its cells through
   * `lift`, and then the floor went away. This is the same removal `lift` does,
   * one pass later, and it is safe there because `cellSim` has already run.
   */
  private clearPlaced(b: Body): void {
    if (!b.placed) return;
    const mask = shapes()[b.shape].masks[b.placedOrient];
    const w = this.world;
    for (let i = 0; i < mask.dx.length; i++) {
      this.workThisTick++;
      if (b.wrote[i] === 0) continue;
      b.wrote[i] = 0;
      const wx = b.placedX + mask.dx[i];
      const wy = b.placedY + mask.dy[i];
      w.writeRaw(wx, wy, VOID, w.getTemp(wx, wy), 0, 0);
    }
    b.placed = false;
  }

  private place(b: Body, ix: number, iy: number, orient: number): void {
    const shape = shapes()[b.shape];
    const mask = shape.masks[orient];
    const dx = mask.dx;
    const dy = mask.dy;
    const src = mask.src;
    const w = this.world;

    // ---- grain window (#121). Cleared and refilled with the pose, because a
    // stale byte from the previous pose would claim a cell this pose does not
    // occupy — and the renderer trusts the window to say which cells are the
    // body's. The clear is 625 bytes for a body that is actually moving; a
    // resting or sleeping body never reaches here.
    const grain = b.grain;
    grain.data.fill(GRAIN_ABSENT);
    grain.ox = ix - GRAIN_HALF;
    grain.oy = iy - GRAIN_HALF;
    grain.version++;
    const shapeGrain = shape.grain;

    for (let i = 0; i < dx.length; i++) {
      this.workThisTick++;
      const p = src[i];
      const m = b.mat[p];
      b.wrote[i] = 0;
      if (m === VOID) continue;
      const wx = ix + dx[i];
      const wy = iy + dy[i];

      const here = w.peek(wx, wy);
      if (here !== VOID) {
        // A crate entering water must **move** the water, not delete it.
        // `particles.promote` already clears the source cell, so matter is
        // conserved by the layer that was written for exactly this — the
        // talk's step 8, and there is no second copy of it here. The impulse
        // is the body's own velocity, so the splash goes where the crate went.
        const moved =
          this.displaceTo !== null &&
          this.displaceTo.promote(wx, wy, b.vx * 0.5, b.vy * 0.5 - 20);
        // Still occupied — something rigid grew into the pose between the
        // collision probe and here. Leave the pixel unplaced rather than
        // overwrite: `wrote[i]` stays 0, so `lift` will not claim the cell.
        if (!moved && w.peek(wx, wy) !== VOID) continue;
      }

      // STATIC on every body cell, awake or asleep. For a sleeping body it is
      // the whole mechanism; for an awake one it guards the window between
      // write-back and the next tick's lift, in which `reactions` and
      // `thermal` run and a crate must not start raining wood.
      w.writeRaw(wx, wy, m, b.temp[p], (b.flags[p] & ~Flag.MOVED) | Flag.STATIC, b.aux[p]);
      b.wrote[i] = 1;
      // Keyed on the canonical pixel `p`, never on `wx`/`wy` — that is the
      // whole of the rotation invariance, and the reason this is a lookup
      // rather than a hash. Written only where the cell was actually taken, so
      // the window and `wrote` agree: a pixel the body could not place is not
      // the body's to colour.
      grain.data[(dy[i] + GRAIN_HALF) * GRAIN_SPAN + (dx[i] + GRAIN_HALF)] = shapeGrain[p];
    }
    b.placedX = ix;
    b.placedY = iy;
    b.placedOrient = orient;
    b.placed = true;
  }

  /**
   * Probe only the cells that lead in direction `d`.
   *
   * A cell can block movement in direction `d` only if the body does not
   * already occupy the cell one step further along `d` — otherwise that
   * neighbour would hit first. That criterion is exact for **any** mask, convex
   * or not, and it is computed at bake time, which is what the precomputed
   * boundary buys: a crate moving right probes about thirty cells instead of
   * sixty-four, and a solid shape probes its right-hand column instead of its
   * whole area.
   */
  private fitsDir(b: Body, ix: number, iy: number, orient: number, d: number): boolean {
    const mask = shapes()[b.shape].masks[orient];
    const list = mask.edge[d];
    const dx = mask.dx;
    const dy = mask.dy;
    const src = mask.src;
    const w = this.world;
    for (let i = 0; i < list.length; i++) {
      this.workThisTick++;
      const e = list[i];
      if (b.mat[src[e]] === VOID) continue;
      const m = w.peek(ix + dx[e], iy + dy[e]);
      if (m !== VOID && M.solid[m] === 1) return false;
    }
    return this.fitsOtherBodies(b, ix, iy, orient);
  }

  /** Every boundary cell. Used when the orientation itself changes. */
  private fitsAll(b: Body, ix: number, iy: number, orient: number): boolean {
    const mask = shapes()[b.shape].masks[orient];
    const bd = mask.boundary;
    const dx = mask.dx;
    const dy = mask.dy;
    const src = mask.src;
    const w = this.world;
    for (let i = 0; i < bd.length; i++) {
      this.workThisTick++;
      const e = bd[i];
      if (b.mat[src[e]] === VOID) continue;
      const m = w.peek(ix + dx[e], iy + dy[e]);
      if (m !== VOID && M.solid[m] === 1) return false;
    }
    return this.fitsOtherBodies(b, ix, iy, orient);
  }

  /**
   * Awake bodies are all out of the grid at once, so they cannot see each
   * other's pixels. They see each other's **tight rotated AABBs** instead,
   * computed at bake time — exact for an axis-aligned crate, conservative for
   * a rotated one. There are at most four of them, so this is twelve integer
   * comparisons at worst.
   *
   * A *sleeping* body needs none of this: its pixels are in the grid, so it is
   * per-pixel to everyone, like any other terrain.
   *
   * **The other body's pose is where it is, not where it was last placed, and
   * the difference was a hole in this function big enough to drive two crates
   * through.** The guard used to read `|| !o.placed`, which skips a body whose
   * cells are out of the grid — and `step` runs *after* `lift`, so at the
   * moment this is asked **every awake, moving body has `placed === false`**.
   * The clause therefore skipped exactly the bodies it exists to see, and the
   * only thing it ever compared against was a body on the resting fast path,
   * which still holds its cells and would have been caught per pixel anyway.
   * Two crates converging in mid-air passed straight through each other and
   * came to rest fifteen cells interpenetrated, and — because `place` refuses
   * a pixel whose cell is already taken — twenty-two cells of wood were
   * deleted from the world in the process.
   *
   * A lifted body's pose is `(round(x), round(y), orient)`, which is where
   * `writeBack` is about to put it. Bodies are stepped in registry order, so
   * one that has already moved this tick is compared at its new pose and one
   * that has not is compared at its old one; that is sequential and therefore
   * deterministic, which is the property the fixture checks.
   */
  private fitsOtherBodies(b: Body, ix: number, iy: number, orient: number): boolean {
    if (this.awakeCount < 2) return true;
    const a = shapes()[b.shape].masks[orient];
    const aMinX = ix + a.minDx;
    const aMaxX = ix + a.maxDx;
    const aMinY = iy + a.minDy;
    const aMaxY = iy + a.maxDy;
    for (let k = 0; k < this.bodies.length; k++) {
      const o = this.bodies[k];
      if (o === b || !o.awake || o.dead) continue;
      const ox = o.placed ? o.placedX : Math.round(o.x);
      const oy = o.placed ? o.placedY : Math.round(o.y);
      const m = shapes()[o.shape].masks[o.placed ? o.placedOrient : o.orient];
      if (aMinX > ox + m.maxDx || aMaxX < ox + m.minDx) continue;
      if (aMinY > oy + m.maxDy || aMaxY < oy + m.minDy) continue;
      return false;
    }
    return true;
  }

  /**
   * Mean horizontal offset of the cells that are actually resting on
   * something. Zero means the support is centred.
   */
  private contactOffset(b: Body, ix: number, iy: number, orient: number): number {
    const mask = shapes()[b.shape].masks[orient];
    const list = mask.edge[DIR_DOWN];
    const dx = mask.dx;
    const dy = mask.dy;
    const src = mask.src;
    const w = this.world;
    let sum = 0;
    let n = 0;
    for (let i = 0; i < list.length; i++) {
      this.workThisTick++;
      const e = list[i];
      if (b.mat[src[e]] === VOID) continue;
      const m = w.peek(ix + dx[e], iy + dy[e] + 1);
      if (m === VOID || M.solid[m] !== 1) continue;
      sum += dx[e];
      n++;
    }
    return n === 0 ? 0 : sum / n;
  }

  private applyCrush(b: Body, shape: BakedShape, moved: number): void {
    const sink = this.crush as CrushSink;
    const mask = shape.masks[b.orient];
    const ix = Math.round(b.x);
    const iy = Math.round(b.y);
    const out = this.victims;
    out.length = 0;
    sink.query(ix + mask.minDx, iy + mask.minDy, ix + mask.maxDx, iy + mask.maxDy, out);
    if (out.length === 0) return;
    const amount = moved * b.live * CRUSH_PER_PIXEL_CELL;
    for (let i = 0; i < out.length; i++) {
      const e = out[i];
      // ==== docs/01 §5.1, the standing rule, in one branch =================
      // "When a simulated interaction can produce an outcome we have not fully
      // tamed, it applies to enemies first and to the player only once stress
      // fixtures prove it cannot spike." A rigid body driven by a destructible
      // world can be handed an impulse nobody authored — a collapsing ceiling
      // dropping a crate four cells in one tick — and the player's death has to
      // stay attributable. So a crate crushes an enemy and deals the player
      // exactly zero, from the identical fixture. The counter exists so a test
      // can tell "declined" apart from "never overlapped".
      if (sink.isPlayer(e)) {
        this.playerCrushesDeclined++;
        continue;
      }
      sink.damage(e, amount, b.id);
    }
  }

  /**
   * Release what is left of a body into the grid as ordinary matter.
   *
   * `Flag.STATIC` is *not* set, which is the entire difference between a crate
   * and a pile of planks: from here `cellsim` and `thermal` own it, exactly as
   * they own everything else, and no debris system was written.
   */
  private breakApart(b: Body): void {
    const shape = shapes()[b.shape];
    const mask = shape.masks[b.placedOrient];
    const w = this.world;
    const inGrid = b.placed;
    for (let i = 0; i < mask.dx.length; i++) {
      this.workThisTick++;
      const p = mask.src[i];
      const m = b.mat[p];
      if (m === VOID) continue;
      const wx = b.placedX + mask.dx[i];
      const wy = b.placedY + mask.dy[i];
      if (inGrid) {
        // The body never left the grid (the resting fast path). Breaking apart
        // is then exactly one thing: stop being static.
        if (b.wrote[i] === 1) w.clearFlag(wx, wy, Flag.STATIC);
        continue;
      }
      // `cellSim` ran between the lift and here, so loose matter may have
      // poured into the space the body vacated. Do not overwrite it.
      if (w.peek(wx, wy) !== VOID) continue;
      w.writeRaw(wx, wy, m, b.temp[p], b.flags[p] & ~(Flag.MOVED | Flag.STATIC), b.aux[p]);
    }
    b.placed = false;
    b.mat.fill(VOID);
    b.live = 0;
    b.dead = true;
    if (b.awake) {
      b.awake = false;
      this.awakeCount--;
    }
    this.unregisterSleeping(b);
    if (this.onBreak !== null) this.onBreak(b.id, b.placedX, b.placedY);
  }

  /**
   * Index a sleeping body's cells, plus the cells immediately outside it.
   *
   * The halo is the difference between "you broke the crate" and "you dug the
   * floor out from under the crate" — both must wake it, and only the first
   * touches a cell the crate owns. It is charged as work because it is the
   * body's most expensive tick, and the quota bounds the worst tick.
   */
  private registerSleeping(b: Body): void {
    const mask = shapes()[b.shape].masks[b.placedOrient];
    const own = this.sleepingCells;
    for (let i = 0; i < mask.dx.length; i++) {
      this.workThisTick++;
      if (b.mat[mask.src[i]] === VOID) continue;
      own.set(packCoord(b.placedX + mask.dx[i], b.placedY + mask.dy[i]), b.id);
    }
    for (let d = 0; d < 4; d++) {
      const list = mask.edge[d];
      const ox = d === DIR_RIGHT ? 1 : d === DIR_LEFT ? -1 : 0;
      const oy = d === DIR_DOWN ? 1 : d === DIR_UP ? -1 : 0;
      for (let i = 0; i < list.length; i++) {
        this.workThisTick++;
        const e = list[i];
        if (b.mat[mask.src[e]] === VOID) continue;
        const key = packCoord(b.placedX + mask.dx[e] + ox, b.placedY + mask.dy[e] + oy);
        if (!own.has(key)) own.set(key, b.id);
      }
    }
  }

  private unregisterSleeping(b: Body): void {
    if (this.sleepingCells.size === 0) return;
    const mask = shapes()[b.shape].masks[b.placedOrient];
    const own = this.sleepingCells;
    for (let i = 0; i < mask.dx.length; i++) {
      own.delete(packCoord(b.placedX + mask.dx[i], b.placedY + mask.dy[i]));
    }
    for (let d = 0; d < 4; d++) {
      const list = mask.edge[d];
      const ox = d === DIR_RIGHT ? 1 : d === DIR_LEFT ? -1 : 0;
      const oy = d === DIR_DOWN ? 1 : d === DIR_UP ? -1 : 0;
      for (let i = 0; i < list.length; i++) {
        const e = list[i];
        const key = packCoord(b.placedX + mask.dx[e] + ox, b.placedY + mask.dy[e] + oy);
        if (own.get(key) === b.id) own.delete(key);
      }
    }
  }

  /** Cells currently indexed as belonging to a sleeping body. Telemetry. */
  get sleepingCellCount(): number {
    return this.sleepingCells.size;
  }
}
